// El dibujo del calendario, compartido por el de la barra y el de las noches de hotel.
//
// Emite los MISMOS nombres de clase que Litepicker (ya prefijados), porque el CSS del buscador
// tiene cuarenta reglas escritas contra ellos y lo que se quiere es que el calendario siga
// viéndose igual.
import { mismoDia, antesDe } from './fechas.js'

// Los mismos nombres que emite Litepicker en es-ES (Intl, weekday corto). Van en minuscula
// porque quien los pone en mayusculas es el CSS heredado, con text-transform: uppercase.
export const DIAS = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom']
export const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

/**
 * Un mes.
 * @param {Date} fecha        cualquier día del mes que se pinta
 * @param {number} cual       0 = el de la izquierda (lleva la flecha atrás), 1 = el derecho
 * @param {object} marcas     {ida, vuelta, soloIda, minimo, hoy}
 */
export function pintarMes (fecha, cual, marcas) {
  const primero = new Date(fecha.getFullYear(), fecha.getMonth(), 1)
  // La semana empieza en lunes, como en la home (lang es-ES).
  const hueco = (primero.getDay() + 6) % 7
  const dias = new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0).getDate()

  // La cabecera, calcada de la que emite Litepicker: los DOS botones en los dos meses --de
  // esconder el que no toca se encarga el CSS heredado--, el nombre en un <strong> y las flechas
  // como SVG, no como los caracteres ‹ ›. Copiarla a ojo dejaba el titulo descentrado y un nodo
  // de mas, y eso se ve.
  const boton = (clase, etiqueta, dibujo) => '<button type="button" class="rvb-' + clase
    + '" aria-label="' + etiqueta + '"><svg width="11" height="16" xmlns="http://www.w3.org/2000/svg">'
    + dibujo + '</svg></button>'

  // Litepicker marca el mes que no puede retroceder, y de esconderle la flecha se encarga el
  // CSS (.month-item.no-previous-month .button-previous-month). Sin esta clase el widget
  // ensenaba una flecha de retroceso que en la home no esta: el calendario arranca en el mes
  // del minimo y de ahi no se puede ir hacia atras.
  const minimo = marcas && marcas.minimo ? marcas.minimo : null
  const sinAtras = !minimo || (fecha.getFullYear() === minimo.getFullYear()
    && fecha.getMonth() === minimo.getMonth())

  let html = '<div class="rvb-month-item' + (sinAtras ? ' rvb-no-previous-month' : '') + '">'
    + '<div class="rvb-month-item-header">'
    + boton('button-previous-month', 'Mes anterior',
      '<path d="M7.919 0l2.748 2.667L5.333 8l5.334 5.333L7.919 16 0 8z" fill-rule="nonzero"></path>')
    + '<div><strong class="rvb-month-item-name">' + MESES[fecha.getMonth()] + '</strong>'
    + '<span class="rvb-month-item-year">' + fecha.getFullYear() + '</span></div>'
    + boton('button-next-month', 'Mes siguiente',
      '<path d="M2.748 16L0 13.333 5.333 8 0 2.667 2.748 0l7.919 8z" fill-rule="nonzero"></path>')
    + '</div><div class="rvb-month-item-weekdays-row">'
    + DIAS.map((d) => '<div>' + d + '</div>').join('')
    + '</div><div class="rvb-container__days">'

  for (let i = 0; i < hueco; i++) html += '<div></div>'
  for (let d = 1; d <= dias; d++) {
    const dia = new Date(fecha.getFullYear(), fecha.getMonth(), d)
    const clases = ['rvb-day-item']
    if (marcas.minimo && antesDe(dia, marcas.minimo)) clases.push('rvb-is-locked')
    if (marcas.hoy && mismoDia(dia, marcas.hoy)) clases.push('rvb-is-today')
    if (mismoDia(dia, marcas.ida)) clases.push('rvb-is-start-date')
    if (!marcas.soloIda && marcas.vuelta) {
      if (mismoDia(dia, marcas.vuelta)) clases.push('rvb-is-end-date')
      if (antesDe(marcas.ida, dia) && antesDe(dia, marcas.vuelta)) clases.push('rvb-is-in-range')
    } else if (marcas.soloIda && mismoDia(dia, marcas.ida)) {
      // SÓLO IDA: el día es principio Y FIN, y hay que decirlo.
      //
      // `is-start-date` no pinta un círculo: pinta el principio de un rango, y por eso lleva un
      // `:after` que es media celda de color hacia el día siguiente --el rabillo que enlaza con
      // la vuelta--. Sin `is-end-date`, en sólo ida ese rabillo se queda ahí solo: un cuadrado
      // pegado a la derecha del día elegido, que es lo que se veía.
      //
      // El CSS ya tiene la regla que lo arregla --`.is-start-date.is-end-date:before, ...:after
      // { display: none }`--, puesta justo para el rango de un día; lo que faltaba era la clase.
      // Se resuelve así, y no apagando el `:after` desde el CSS, porque es lo que hace el
      // buscador de siempre: Litepicker marca el mismo día como inicio y fin.
      clases.push('rvb-is-end-date')
    }
    html += '<div class="' + clases.join(' ') + '" role="button" tabindex="-1"'
      + ' data-time="' + dia.getTime() + '"'
      + ' aria-label="' + d + ' de ' + MESES[fecha.getMonth()] + ' de ' + fecha.getFullYear() + '">'
      + d + '</div>'
  }
  return html + '</div></div>'
}

/**
 * Los dos meses (uno solo en móvil), envueltos como los envuelve Litepicker.
 */
export function pintarCalendario (mesVisible, marcas) {
  const unMes = window.innerWidth <= 768
  const segundo = new Date(mesVisible.getFullYear(), mesVisible.getMonth() + 1, 1)
  return '<div class="rvb-litepicker"><div class="rvb-container__main">'
    + '<div class="rvb-container__months' + (unMes ? '' : ' rvb-columns-2') + '">'
    + pintarMes(mesVisible, 0, marcas) + (unMes ? '' : pintarMes(segundo, 1, marcas))
    + '</div></div></div>'
}

/**
 * El clic dentro de un calendario: devuelve qué hacer, sin tocar nada.
 * @returns {{dia?: Date, mes?: number}|null}  mes: -1 anterior, +1 siguiente
 */
export function leerClic (e) {
  const dia = e.target.closest('.rvb-day-item')
  if (dia && !dia.classList.contains('rvb-is-locked')) return { dia: new Date(Number(dia.dataset.time)) }
  if (e.target.closest('.rvb-button-previous-month')) return { mes: -1 }
  if (e.target.closest('.rvb-button-next-month')) return { mes: 1 }
  return null
}
